import { AutomationAction } from './ast/AutomationAction.js';
import { getServiceDataSuggestions } from '../utils/hassServiceData.js';

export const MEDIA_SOURCE_TYPES = [
  { value: 'media_source', label: 'Media source' },
  { value: 'url', label: 'Web URL' },
  { value: 'manual', label: 'Manual' },
];

export const DEFAULT_MEDIA_CONTENT_TYPE = 'music';

/** Home Assistant media_player services supported by the friendly editor. */
export const MEDIA_PLAYER_SERVICES = [
  { value: 'play_media',          label: 'Play media' },
  { value: 'media_play',          label: 'Play' },
  { value: 'media_pause',         label: 'Pause' },
  { value: 'media_play_pause',    label: 'Play/Pause' },
  { value: 'media_stop',          label: 'Stop' },
  { value: 'media_next_track',    label: 'Next track' },
  { value: 'media_previous_track', label: 'Previous track' },
  { value: 'media_seek',          label: 'Seek' },
  { value: 'volume_set',          label: 'Volume set' },
  { value: 'volume_up',           label: 'Volume up' },
  { value: 'volume_down',         label: 'Volume down' },
  { value: 'volume_mute',         label: 'Mute/unmute' },
];

export function isMediaPlayerService(service) {
  return MEDIA_PLAYER_SERVICES.some(option => option.value === service);
}

/** Reuse the existing service-data catalogue for non-play_media fields. */
export function mediaPlayerDataFields(service) {
  return getServiceDataSuggestions('media_player', service);
}

export function defaultMediaPlayerData(service, existingData = {}, parameters = []) {
  const fields = mediaPlayerDataFields(service);
  if (fields.length === 0) return null;
  return Object.fromEntries(fields.map(field => {
    const parameter = parameters.find(item => item.key === field.parameter);
    return [field.parameter, existingData[field.parameter] ?? parameter?.value?.toString() ?? field.value ?? ''];
  }));
}

const SOURCE_TYPE_VALUES = new Set(MEDIA_SOURCE_TYPES.map(option => option.value));
const MEDIA_METADATA_KEYS = [
  'title',
  'thumbnail',
  'media_class',
  'children_media_class',
  'navigateIds',
];

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
  }
  return value;
}

function valueFromParameter(action, key) {
  const parameter = action.parameters?.find(item => item.key === key);
  return parameter?.value?.toString() ?? '';
}

function entityFromAction(action) {
  const entityId = action.target?.entity_id ?? valueFromParameter(action, 'entity_id');
  if (Array.isArray(entityId)) return entityId.join(', ');
  return entityId == null ? '' : String(entityId);
}

function dataFromAction(action) {
  if (action.data && typeof action.data === 'object' && !Array.isArray(action.data)) {
    return action.data;
  }
  return Object.fromEntries(
    (action.parameters ?? [])
      .filter(parameter => parameter.key && parameter.key !== 'entity_id')
      .map(parameter => [parameter.key, parameter.value?.toString() ?? ''])
  );
}

function mediaPayloadFromAction(action) {
  const data = dataFromAction(action);
  const nested = data.media && typeof data.media === 'object' && !Array.isArray(data.media);
  return { data, media: nested ? data.media : data };
}

function parseBoolean(value) {
  if (value === true || value === false) return value;
  if (String(value).toLowerCase() === 'true') return true;
  if (String(value).toLowerCase() === 'false') return false;
  return null;
}

export function detectMediaSourceType(mediaContentId) {
  const value = String(mediaContentId ?? '').trim();
  if (value.startsWith('media-source://')) return 'media_source';
  if (/^https?:\/\//i.test(value)) return 'url';
  return 'manual';
}

/**
 * Return the editor draft for a generic media_player.play_media action.
 * TTS actions deliberately return null so the existing TTS editor keeps its
 * richer representation.
 */
export function parseMediaPlaybackAction(action) {
  if (!action || action.provider !== 'media_player' || action.service !== 'play_media') return null;

  const { data, media } = mediaPayloadFromAction(action);
  const mediaContentId = media.media_content_id == null ? '' : String(media.media_content_id);
  if (media.media_content_type === 'provider' && mediaContentId.startsWith('media-source://tts/')) return null;

  const mediaContentType = media.media_content_type == null || media.media_content_type === ''
    ? DEFAULT_MEDIA_CONTENT_TYPE
    : String(media.media_content_type);
  const metadata = action.editorMetadata;
  const sourceType = SOURCE_TYPE_VALUES.has(metadata?.sourceType)
    ? metadata.sourceType
    : detectMediaSourceType(mediaContentId);

  return {
    entityId: entityFromAction(action),
    sourceType,
    mediaContentId,
    mediaContentType,
    metadata: clone(media.metadata ?? {}),
    announce: Object.hasOwn(data, 'announce') ? parseBoolean(data.announce) : null,
    enqueue: data.enqueue == null ? '' : String(data.enqueue),
    isNew: false,
  };
}

/** Keep only the metadata belonging to one selected item, never its children. */
export function mediaMetadataFromBrowseItem(item, navigateIds = []) {
  const metadata = {};
  for (const key of MEDIA_METADATA_KEYS) {
    const value = item?.metadata?.[key] ?? item?.[key];
    if (value !== undefined) {
      metadata[key] = clone(value);
    }
  }
  if (navigateIds.length > 0 && metadata.navigateIds === undefined) {
    metadata.navigateIds = clone(navigateIds);
  }
  return metadata;
}

/**
 * Build the persisted generic media playback action. `sourceType` is editor
 * metadata only; Home Assistant receives only its media_content_* fields.
 */
export function buildMediaPlaybackAction({
  entityId = '',
  mediaContentId = '',
  mediaContentType = DEFAULT_MEDIA_CONTENT_TYPE,
  announce = null,
  enqueue = '',
  sourceType = detectMediaSourceType(mediaContentId),
  metadata = {},
} = {}) {
  const media = {
    media_content_id: String(mediaContentId ?? ''),
    media_content_type: String(mediaContentType ?? DEFAULT_MEDIA_CONTENT_TYPE),
  };
  if (metadata && typeof metadata === 'object' && Object.keys(metadata).length > 0) {
    media.metadata = clone(metadata);
  }
  const data = { media };
  if (announce === true || announce === false) data.announce = announce;
  if (String(enqueue ?? '').trim()) data.enqueue = String(enqueue);

  return new AutomationAction({
    type: 'call_service',
    provider: 'media_player',
    service: 'play_media',
    target: { entity_id: entityId },
    data,
    editorMetadata: {
      type: 'media_player',
      operation: 'play_media',
      sourceType: SOURCE_TYPE_VALUES.has(sourceType) ? sourceType : detectMediaSourceType(mediaContentId),
    },
  });
}

export function isMediaPlaybackAction(action) {
  return parseMediaPlaybackAction(action) !== null;
}
